/**
 * Permission codes the Projects screens declare an interest in.
 *
 * These are the dot-strings `internal/projects/permissions.go` registers with
 * the RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them
 * once here means a renamed permission is a compile error rather than a
 * silently invisible button.
 *
 * # The one that is not what it looks like
 *
 * `timesheetSelfService` is `notification.read`, and that is deliberate on the
 * server's side, not a mistake here. Every ESS timesheet route — create, read,
 * edit, submit and discard **one's own** weekly sheet — is mounted against it
 * because the route registry requires every tenant route to name *some*
 * permission and there is no "any authenticated user" option. The real
 * authorization is `requireTimesheetOwner` in `internal/projects/timesheet.go`:
 * acting on somebody **else's** sheet additionally needs `timesheetManage`.
 * Every system role including the bare Employee already holds `notification.read`,
 * so ESS works with no extra grant.
 *
 * That shapes the screens: the "My week" surface is offered to anyone signed in,
 * while the employee picker on it appears only for a holder of `timesheetManage`,
 * and the timesheets **list** across the whole tenant needs `timesheetRead`.
 *
 * # The approval trio
 *
 * `timesheetApprove` is the permission that **posts to the ledger**. Approval is
 * the only path that flips a timesheet to `approved`, and the same service call
 * resolves each line's cost rate and posts Dr Project WIP / Cr Payroll clearing
 * (`internal/projects/doc.go`). It is therefore treated on screen like a Post
 * button and never like a tick box — the accounts it will hit are named before
 * the button, not after it.
 *
 * `billingManage` is the "commits the business to an invoice" permission: it
 * covers milestone CRUD **and** running time-and-materials and percent-complete
 * billing, all three of which raise, confirm and post a real AR invoice.
 *
 * # Other modules' codes, and why these screens cannot do without them
 *
 *  - `ar.customer.read` — a project names the customer it bills.
 *  - `ar.invoice.read` — every billing row links to the AR invoice it raised;
 *    without it the billing history can name an invoice but not open it.
 *  - `gl.journal.read` — an approved timesheet carries `journalEntryId`, the
 *    proof that costing actually posted. The link is the drill-down §8.13 asks
 *    for from an actual back to its transaction.
 *  - `procurement.po.read` — profitability's **commitment** figure is the still-open
 *    portion of purchase-order lines coded to the project (`internal/projects/doc.go`).
 *    It comes from procurement's own bookkeeping, not from `internal/budget`.
 *  - `ap.invoice.read` — the other half of "actual": posted AP invoice lines
 *    tagged to the project.
 *  - `org.account.read` / `org.company.read` — the WIP, payroll-clearing and
 *    revenue accounts, and the company that scopes every project.
 *  - `admin.user.list` — the directory behind the manager, member and employee
 *    pickers.
 */

export const PROJECTS_PERMISSIONS = {
  /** Configure the WIP and payroll-clearing accounts timesheet costing posts to. */
  settingsManage: 'projects.settings.manage',

  projectRead: 'projects.project.read',
  projectCreate: 'projects.project.create',
  projectUpdate: 'projects.project.update',
  projectDelete: 'projects.project.delete',

  phaseManage: 'projects.phase.manage',

  taskRead: 'projects.task.read',
  taskCreate: 'projects.task.create',
  taskUpdate: 'projects.task.update',
  taskDelete: 'projects.task.delete',

  /** Assign the team and their cost/bill rates — the rates costing resolves against. */
  memberManage: 'projects.member.manage',

  /** See everybody's timesheets, not only one's own. */
  timesheetRead: 'projects.timesheet.read',
  /** Declared by the server but never gating a route; see the note above. */
  timesheetCreate: 'projects.timesheet.create',
  /** Key in a timesheet on another employee's behalf. */
  timesheetManage: 'projects.timesheet.manage',
  /** Approving a timesheet posts its cost. Treated as a Post, not a tick. */
  timesheetApprove: 'projects.timesheet.approve',

  /** Milestones, T&M billing and percent-complete recognition — all raise AR invoices. */
  billingManage: 'projects.billing.manage',

  reportRead: 'projects.report.read',

  /**
   * What every ESS timesheet route actually declares (`notification.read`).
   * Held by every role; ownership is enforced in the service layer.
   */
  timesheetSelfService: 'notification.read',

  /* ------------------------------------------------- other modules' codes */

  customerRead: 'ar.customer.read',
  arInvoiceRead: 'ar.invoice.read',
  apInvoiceRead: 'ap.invoice.read',
  /** The journal an approved timesheet posted. */
  journalRead: 'gl.journal.read',
  /** Where profitability's commitment figure comes from. */
  purchaseOrderRead: 'procurement.po.read',
  accountRead: 'org.account.read',
  companyRead: 'org.company.read',
  costCenterRead: 'org.unit.read',
} as const

export type ProjectsPermission =
  (typeof PROJECTS_PERMISSIONS)[keyof typeof PROJECTS_PERMISSIONS]

/**
 * The contract, schedule and resource permissions C11 added
 * (`internal/projects/{baseline,variation,wip,retention,advance,resource,
 * allocation,capacity,schedule,risk,subcontract,chargeability}.go`).
 *
 * They are a separate constant from `PROJECTS_PERMISSIONS` above because they
 * are a separate grant set with a separate shape, and three of the splits are
 * load-bearing rather than bureaucratic:
 *
 *  - **`baseline.manage` ⊗ `baseline.submit` ⊗ `baseline.approve`.** Drawing a
 *    plan, asking for it to be blessed, and blessing it are three acts, and
 *    §6's segregation of duties is the reason a planner cannot approve their
 *    own baseline.
 *  - **`variation.approve` ⊗ `variation.accept`.** Internal approval says the
 *    business is willing to do the work; acceptance records that the CUSTOMER
 *    agreed to pay for it. Only the second produces a new baseline, and the two
 *    are held by different people.
 *  - **`wip.run` ⊗ `wip.post`.** Computing a WIP position is arithmetic;
 *    posting it writes a journal. The post button is treated on screen like
 *    every other Post: it names what it will do before it does it.
 *
 * `chargeability.override` deserves its own note. It does not grant time
 * booking — it grants the right to switch a control OFF for a stated window,
 * which is why the override screen shows the expiry date on the same line as
 * the reason.
 */
export const PROJECTS_C11_PERMISSIONS = {
  baselineRead: 'projects.baseline.read',
  baselineManage: 'projects.baseline.manage',
  baselineSubmit: 'projects.baseline.submit',
  baselineApprove: 'projects.baseline.approve',

  variationRead: 'projects.variation.read',
  variationManage: 'projects.variation.manage',
  variationApprove: 'projects.variation.approve',
  variationAccept: 'projects.variation.accept',

  wipRead: 'projects.wip.read',
  wipRun: 'projects.wip.run',
  wipPost: 'projects.wip.post',

  retentionRead: 'projects.retention.read',
  retentionManage: 'projects.retention.manage',
  retentionRelease: 'projects.retention.release',

  advanceManage: 'projects.advance.manage',

  resourceRead: 'projects.resource.read',
  resourceManage: 'projects.resource.manage',
  capacityManage: 'projects.capacity.manage',

  allocationRead: 'projects.allocation.read',
  allocationManage: 'projects.allocation.manage',

  scheduleManage: 'projects.schedule.manage',

  riskManage: 'projects.risk.manage',
  subcontractManage: 'projects.subcontract.manage',

  chargeabilityRead: 'projects.chargeability.read',
  chargeabilityOverride: 'projects.chargeability.override',
} as const

export type ProjectsC11Permission =
  (typeof PROJECTS_C11_PERMISSIONS)[keyof typeof PROJECTS_C11_PERMISSIONS]
